'use client';

import React, { useState } from 'react';

interface Trip {
  id: number;
  property: string;
  location: string;
  image: string;
  imageAlt: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  total: string;
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  bookingRef: string;
  host: string;
  rating?: number;
}

const trips: Trip[] = [
  {
    id: 1,
    property: 'Luxury Villa Kololo',
    location: 'Kampala, Uganda',
    image: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400&h=250&fit=crop',
    imageAlt: 'Luxury villa with swimming pool in Kololo Kampala',
    checkIn: 'Aug 25, 2026',
    checkOut: 'Aug 30, 2026',
    nights: 5,
    total: 'UGX 2,500,000',
    status: 'upcoming',
    bookingRef: 'NDT-2026-0821',
    host: 'James Ssekandi',
  },
  {
    id: 2,
    property: 'Lakeside Retreat Entebbe',
    location: 'Entebbe, Uganda',
    image: 'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?w=400&h=250&fit=crop',
    imageAlt: 'Lakeside retreat with views of Lake Victoria in Entebbe',
    checkIn: 'Sep 5, 2026',
    checkOut: 'Sep 8, 2026',
    nights: 3,
    total: 'UGX 1,200,000',
    status: 'upcoming',
    bookingRef: 'NDT-2026-0834',
    host: 'Grace Achieng',
  },
  {
    id: 3,
    property: 'Safari Lodge Murchison',
    location: 'Murchison Falls, Uganda',
    image: 'https://images.unsplash.com/photo-1493246507139-91e8fad9978e?w=400&h=250&fit=crop',
    imageAlt: 'Safari lodge surrounded by savanna at Murchison Falls National Park',
    checkIn: 'Jul 10, 2026',
    checkOut: 'Jul 14, 2026',
    nights: 4,
    total: 'UGX 3,200,000',
    status: 'completed',
    bookingRef: 'NDT-2026-0712',
    host: 'David Ochieng',
    rating: 5,
  },
  {
    id: 4,
    property: 'Mountain Cabin Rwenzori',
    location: 'Kasese, Uganda',
    image: 'https://images.unsplash.com/photo-1542718610-a1d656d1884c?w=400&h=250&fit=crop',
    imageAlt: 'Cozy mountain cabin with Rwenzori mountain views in Kasese',
    checkIn: 'May 20, 2026',
    checkOut: 'May 23, 2026',
    nights: 3,
    total: 'UGX 900,000',
    status: 'completed',
    bookingRef: 'NDT-2026-0520',
    host: 'Ruth Kemigisha',
    rating: 4,
  },
];

const statusConfig: Record<string, { label: string; color: string }> = {
  upcoming: { label: 'Upcoming', color: 'bg-blue-100 text-blue-700' },
  ongoing: { label: 'Ongoing', color: 'bg-green-100 text-green-700' },
  completed: { label: 'Completed', color: 'bg-gray-100 text-gray-600' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-600' },
};

export default function GuestTrips() {
  const [filter, setFilter] = useState<'all' | 'upcoming' | 'completed'>('all');

  const filtered = trips.filter((t) => {
    if (filter === 'all') return true;
    if (filter === 'upcoming') return t.status === 'upcoming' || t.status === 'ongoing';
    return t.status === 'completed' || t.status === 'cancelled';
  });

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Trips', value: '6', icon: '✈️' },
          { label: 'Upcoming', value: '2', icon: '📅' },
          { label: 'Countries', value: '3', icon: '🌍' },
          { label: 'Nights Stayed', value: '18', icon: '🌙' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="text-2xl mb-2">{stat.icon}</div>
            <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {(['all', 'upcoming', 'completed'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-full text-sm font-semibold capitalize transition-all ${
              filter === f ? 'bg-[#4088C6] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#4088C6]/40'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Trips Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {filtered.map((trip) => (
          <div key={trip.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
            <div className="relative h-44">
              <img src={trip.image} alt={trip.imageAlt} className="w-full h-full object-cover" />
              <div className="absolute top-3 left-3">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusConfig[trip.status].color}`}>
                  {statusConfig[trip.status].label}
                </span>
              </div>
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-bold text-gray-900 text-base">{trip.property}</h3>
                  <p className="text-sm text-gray-500">{trip.location}</p>
                </div>
                {trip.rating && (
                  <div className="flex items-center gap-1 bg-amber-50 px-2 py-1 rounded-lg">
                    <span className="text-amber-500 text-sm">★</span>
                    <span className="text-sm font-semibold text-amber-700">{trip.rating}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                <span>📅 {trip.checkIn} – {trip.checkOut}</span>
                <span>🌙 {trip.nights} nights</span>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <div>
                  <p className="text-xs text-gray-400">Booking ref</p>
                  <p className="text-xs font-mono font-semibold text-gray-700">{trip.bookingRef}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">Total paid</p>
                  <p className="text-sm font-bold text-gray-900">{trip.total}</p>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                {trip.status === 'upcoming' && (
                  <>
                    <button className="flex-1 py-2 text-xs font-semibold text-[#4088C6] border border-[#4088C6]/30 rounded-xl hover:bg-[#4088C6]/5 transition-colors">
                      View Details
                    </button>
                    <a href="/messages" className="flex-1 py-2 text-xs font-semibold text-center text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                      Message Host
                    </a>
                  </>
                )}
                {trip.status === 'completed' && !trip.rating && (
                  <button className="flex-1 py-2 text-xs font-semibold text-white bg-[#4088C6] rounded-xl hover:bg-[#3070a6] transition-colors">
                    Leave a Review
                  </button>
                )}
                {trip.status === 'completed' && trip.rating && (
                  <button className="flex-1 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                    Book Again
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
